import useSWR from "swr";
import { useMemo } from "react";
import {
  ListOffer,
  ReviewOffer,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  SplatLoan,
  SplatProspect,
} from "../../../amplify/backend/function/OfferExpContacts/ts/public/types";
import { SplatProspectResponse } from "../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import type { CMSCheckoutStep } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { SplatCheckoutDocument } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { getWithAuth } from "utils/dataUtils";
import { useAuthToken } from "hooks/useAuthToken";

const useOfferList = (
  prospectId: string,
  cognitoToken?: string,
  proposalToken?: string
) => {
  const shouldFetch = useMemo(
    () => Boolean((prospectId && cognitoToken) || proposalToken),
    [prospectId, cognitoToken, proposalToken]
  );

  const {
    data: offerList,
    isLoading: isOfferListLoading,
    error: offerListError,
  } = useSWR(
    shouldFetch &&
      (cognitoToken
        ? ["Get List Offer", `/offers/prospect/${prospectId}`, cognitoToken]
        : [
            "Get Customer List Offer",
            `/customer/${proposalToken}`,
            proposalToken,
          ]),
    getWithAuth<ListOffer[]>
  );
  return { offerList, offerListError, isOfferListLoading };
};

const useOffer = (
  offerId?: string,
  cognitoToken?: string,
  proposalToken?: string
) => {
  const shouldFetch = Boolean(offerId && (cognitoToken || proposalToken));
  const {
    data: offer,
    error: offerError,
    isLoading: isOfferLoading,
  } = useSWR(
    shouldFetch &&
      (cognitoToken
        ? ["Get Offer", `/offers/${offerId}`, cognitoToken]
        : [
            "Get Customer Offer",
            `/customer/${proposalToken}/${offerId}`,
            proposalToken,
          ]),
    getWithAuth<ReviewOffer>
  );

  return { offer, offerError, isOfferLoading };
};

const useLoans = (
  prospectId?: string,
  hybridToken?: string,
  orderByOldToNew = false,
  proposalToken?: string,
  enabled = true
): {
  loans?: SplatLoan[];
  isLoansLoading: boolean;
  loansError: Error | null;
  refetchLoans: () => void;
  isValidatingLoans?: boolean;
} => {
  const shouldFetch = enabled && prospectId && (hybridToken || proposalToken);
  const {
    data: loans,
    error: loansError,
    isLoading: isLoansLoading,
    mutate: refetchLoans,
    isValidating: isValidatingLoans,
  } = useSWR(
    shouldFetch &&
      (hybridToken
        ? ["Get Loans", `/prospect/${prospectId}/loan`, hybridToken]
        : [
            "Customer Get Loans",
            `/customer/prospect/${prospectId}/loan`,
            proposalToken,
          ]),
    getWithAuth<SplatLoan[]>
  );

  return {
    loans: orderByOldToNew ? [...(loans || [])]?.reverse() : loans,
    loansError,
    isLoansLoading,
    refetchLoans,
    isValidatingLoans,
  };
};

const useLoanApplicationUrl = (
  prospectId?: string,
  proposalId?: string,
  hybridToken?: string,
  coBorrowerId?: string,
  proposalToken?: string
): {
  loanApplicationUrl?: string;
  isLoanUrlLoading: boolean;
  loanUrlError: Error | null;
  isLoanUrlValidating?: boolean;
} => {
  const shouldFetch =
    prospectId && proposalId && (hybridToken || proposalToken);
  const urlKey = `${
    proposalToken ? "/customer" : ""
  }/prospect/${prospectId}/proposal/${proposalId}/loan/application/url${
    coBorrowerId ? `?coborrower=${coBorrowerId}` : ""
  }`;

  const {
    data,
    error: loanUrlError,
    isLoading: isLoanUrlLoading,
    isValidating: isLoanUrlValidating,
  } = useSWR(
    shouldFetch &&
      (hybridToken
        ? ["Get Loan Application URL", urlKey, hybridToken]
        : ["Customer Get Loan Application URL", urlKey, proposalToken]),
    getWithAuth<{
      url: string;
    }>
  );

  return {
    loanApplicationUrl: data?.url,
    loanUrlError,
    isLoanUrlLoading,
    isLoanUrlValidating,
  };
};

const useProspect = (
  prospectId?: string
): {
  data?: SplatProspectResponse | SplatProspect;
  isLoading: boolean;
  error: Error | null;
} => {
  const hybridAuth = useAuthToken("HYBRID");
  const customerAuth = useAuthToken("PROPOSAL");
  const shouldFetch = (prospectId && hybridAuth) || customerAuth;
  const { data, error, isLoading } = useSWR(
    shouldFetch &&
      (hybridAuth
        ? ["Get Prospect", `/prospect/${prospectId}`, hybridAuth.token]
        : [
            "Get Customer Prospect",
            `/customer/${customerAuth!.token}/prospect`,
            customerAuth!.token,
          ]),
    getWithAuth<SplatProspectResponse>
  );
  return {
    data,
    isLoading,
    error,
  };
};

const useCMSCheckoutSteps = (stepId?: string) => {
  const hybridAuth = useAuthToken("HYBRID");
  const shouldFetch = stepId && hybridAuth;
  const { data, error, isLoading } = useSWR(
    shouldFetch && [
      "Get CMS Checkout Steps",
      `/cmsCheckoutSteps/${stepId}`,
      hybridAuth.token,
    ],
    getWithAuth<CMSCheckoutStep>
  );

  return { data, error, isLoading };
};

const useCheckoutAttachments = (
  prospectId: string,
  proposalId: string,
  documentType: string
) => {
  const hybridAuth = useAuthToken("HYBRID");
  const customerAuth = useAuthToken("PROPOSAL");
  const shouldFetch =
    proposalId && ((prospectId && hybridAuth) || customerAuth);
  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch &&
      (hybridAuth
        ? [
            "Get Checkout Attachments",
            `/prospect/${prospectId}/proposals/${proposalId}/attachments?documentType=${documentType}`,
            hybridAuth.token,
          ]
        : [
            "Get Customer Checkout Attachments",
            `/customer/${
              customerAuth!.token
            }/proposals/${proposalId}/attachments?documentType=${documentType}`,
            customerAuth!.token,
          ]),
    getWithAuth<SplatCheckoutDocument[]>
  );

  return { data, error, isLoading, mutate };
};
const useCheckoutAttachmentsNoType = (
  prospectId: string,
  proposalId: string
) => {
  const hybridAuth = useAuthToken("HYBRID");
  const shouldFetch = prospectId && proposalId && hybridAuth;
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    shouldFetch && [
      "Get All Checkout Attachments",
      `/prospect/${prospectId}/proposals/${proposalId}/attachments`,
      hybridAuth.token,
    ],
    getWithAuth<SplatCheckoutDocument[]>
  );

  return { data, error, isLoading, isValidating, mutate };
};

export {
  useOfferList,
  useOffer,
  useLoans,
  useLoanApplicationUrl,
  useProspect,
  useCMSCheckoutSteps,
  useCheckoutAttachments,
  useCheckoutAttachmentsNoType,
};
